import React, { useState } from 'react';

import { Statement, CatalogService, CatalogModule, ResourceCatalog } from '../models/policy.model';
import { StateLanguage } from '../languages/config/StateLanguage';

interface StatementBuilderProps {
  statements: Statement[];
  catalog: ResourceCatalog | null;
  catalogLoading: boolean;
  readOnly?: boolean;
  onChange: (statements: Statement[]) => void;
}

const StatementBuilder: React.FunctionComponent<StatementBuilderProps> = ({
  statements,
  catalog,
  catalogLoading,
  readOnly = false,
  onChange
}) => {
  const {lang} = StateLanguage()
  const [advancedMode, setAdvancedMode] = useState<Record<number, boolean>>({})

  const updateStatement = (index: number, field: keyof Statement, value: any) => {
    const updated = [...statements]
    updated[index] = { ...updated[index], [field]: value }
    onChange(updated)
  }

  const addStatement = () => {
    onChange([...statements, { effect: 'Allow', actions: [], resources: [] }])
  }

  const removeStatement = (index: number) => {
    onChange(statements.filter((_, i) => i !== index))
  }

  const toggleAction = (stmtIndex: number, action: string) => {
    const stmt = statements[stmtIndex]
    const actions = stmt.actions.includes(action)
      ? stmt.actions.filter(a => a !== action)
      : [...stmt.actions, action]
    updateStatement(stmtIndex, 'actions', actions)
  }

  const addResource = (stmtIndex: number, resource: string) => {
    const stmt = statements[stmtIndex]
    if (!stmt.resources.includes(resource)) {
      updateStatement(stmtIndex, 'resources', [...stmt.resources, resource])
    }
  }

  const removeResource = (stmtIndex: number, resource: string) => {
    const stmt = statements[stmtIndex]
    updateStatement(stmtIndex, 'resources', stmt.resources.filter(r => r !== resource))
  }

  const toggleAdvanced = (index: number) => {
    setAdvancedMode(prev => ({ ...prev, [index]: !prev[index] }))
  }

  const handleAdvancedInput = (stmtIndex: number, value: string) => {
    const resources = value.split(',').map(r => r.trim()).filter(Boolean)
    updateStatement(stmtIndex, 'resources', resources)
  }

  const getModuleLabel = (resource: string): string => {
    if (!catalog) return resource
    for (const svc of catalog.resources) {
      for (const mod of svc.modules) {
        if (mod.resource === resource) {
          return `${svc.label} / ${mod.label}`
        }
      }
    }
    return resource
  }

  const getAvailableActions = (stmtIndex: number): string[] => {
    const stmt = statements[stmtIndex]
    if (!catalog || stmt.resources.length === 0) {
      return catalog?.actions ?? []
    }
    const actionSet = new Set<string>()
    for (const res of stmt.resources) {
      if (res === '*') {
        (catalog.actions ?? []).forEach(a => actionSet.add(a))
      } else {
        for (const svc of catalog.resources) {
          for (const mod of svc.modules) {
            if (mod.resource === res) {
              (mod.actions ?? []).forEach(a => actionSet.add(a))
            }
          }
        }
      }
    }
    return actionSet.size > 0 ? Array.from(actionSet) : catalog.actions ?? []
  }

  const renderReadOnly = (stmt: Statement, index: number) => (
    <div key={index} className="border border-dashed rounded p-4 mb-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="fw-bolder mb-0">Statement {index + 1}</h6>
      </div>
      <div className="mb-2">
        <span className="fw-bolder">{lang.labels.effect}:</span>{' '}
        <span className={`badge ${stmt.effect === 'Allow' ? 'badge-light-success' : 'badge-light-danger'}`}>{stmt.effect}</span>
      </div>
      <div className="mb-2">
        <span className="fw-bolder">{lang.labels.actionsList}:</span>{' '}
        {stmt.actions.map((a, ai) => <span key={ai} className="badge badge-light me-1">{a}</span>)}
      </div>
      <div className="mb-0">
        <span className="fw-bolder">{lang.labels.resources}:</span>{' '}
        {stmt.resources.map((r, ri) => (
          <span key={ri} className="badge badge-light me-1" title={r}>{getModuleLabel(r)}</span>
        ))}
      </div>
    </div>
  )

  const renderResourceSelector = (stmtIndex: number, stmt: Statement) => {
    if (!catalog || catalog.resources.length === 0) {
      return (
        <div className="text-muted fs-7">
          {catalogLoading ? lang.labels.loadingCatalog : lang.labels.emptyCatalog}
        </div>
      )
    }

    return (
      <div>
        <div className="mb-3">
          {stmt.resources.map((r) => (
            <span key={r} className="badge badge-light-primary me-2 mb-2 p-2">
              {getModuleLabel(r)}
              <button
                type="button"
                className="btn btn-icon btn-sm btn-active-light-danger ms-2 p-0"
                style={{width: '16px', height: '16px'}}
                onClick={() => removeResource(stmtIndex, r)}
              >
                <span className="svg-icon svg-icon-7">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)" fill="currentColor"/>
                    <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)" fill="currentColor"/>
                  </svg>
                </span>
              </button>
            </span>
          ))}
        </div>
        {catalog.resources.map((svc) => {
          const availableModules = svc.modules.filter(m => !stmt.resources.includes(m.resource))
          if (availableModules.length === 0) return null
          return (
            <div key={svc.service} className="mb-2">
              <div className="fw-bold text-muted fs-8 mb-1">{svc.label}</div>
              <div className="d-flex flex-wrap gap-1">
                {availableModules.map((mod) => (
                  <button
                    key={mod.resource}
                    type="button"
                    className="btn btn-sm btn-outline btn-outline-dashed btn-outline-primary py-1 px-2 fs-8"
                    onClick={() => addResource(stmtIndex, mod.resource)}
                    title={mod.resource}
                  >
                    + {mod.label}
                  </button>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  const renderEditable = (stmt: Statement, index: number) => {
    const isAdvanced = advancedMode[index] ?? false
    const availableActions = getAvailableActions(index)

    return (
      <div key={index} className="border border-dashed rounded p-4 mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="fw-bolder mb-0">Statement {index + 1}</h6>
          <div className="d-flex gap-2">
            <button
              type="button"
              className={`btn btn-sm ${isAdvanced ? 'btn-light-warning' : 'btn-light'}`}
              onClick={() => toggleAdvanced(index)}
            >
              {lang.labels.advancedMode}
            </button>
            {statements.length > 1 &&
              <button className="btn btn-sm btn-icon btn-light-danger" type="button" onClick={() => removeStatement(index)}>
                <span className="svg-icon svg-icon-3">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)" fill="black"/>
                    <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)" fill="black"/>
                  </svg>
                </span>
              </button>
            }
          </div>
        </div>
        <div className="mb-3">
          <label className="form-label fw-bolder">{lang.labels.effect}</label>
          <select
            className="form-select"
            value={stmt.effect}
            onChange={(e) => updateStatement(index, 'effect', e.target.value)}
          >
            <option value="Allow">{lang.labels.allow}</option>
            <option value="Deny">{lang.labels.deny}</option>
          </select>
        </div>
        <div className="mb-3">
          <label className="form-label fw-bolder">{lang.labels.resources}</label>
          {isAdvanced
            ?
            <div>
              <input
                type="text"
                className="form-control"
                placeholder="digital:admin:company:*, digital:crm:entity:contact:*"
                value={stmt.resources.join(', ')}
                onChange={(e) => handleAdvancedInput(index, e.target.value)}
              />
              <div className="text-muted fs-8 mt-1">{lang.labels.resourcesHint}</div>
            </div>
            :
            renderResourceSelector(index, stmt)
          }
        </div>
        <div className="mb-3">
          <label className="form-label fw-bolder">{lang.labels.actionsList}</label>
          <div className="d-flex flex-wrap gap-2">
            {availableActions.map((action) => (
              <label key={action} className={`btn btn-sm ${stmt.actions.includes(action) ? 'btn-primary' : 'btn-light'}`}>
                <input
                  type="checkbox"
                  className="d-none"
                  checked={stmt.actions.includes(action)}
                  onChange={() => toggleAction(index, action)}
                />
                {action}
              </label>
            ))}
          </div>
        </div>
        <div className="mb-0">
          <label className="form-label fw-bolder text-muted fs-8">{lang.labels.preview}</label>
          <pre className="bg-light-dark rounded p-3 fs-8 mb-0" style={{whiteSpace: 'pre-wrap'}}>
            {JSON.stringify({effect: stmt.effect, actions: stmt.actions, resources: stmt.resources}, null, 2)}
          </pre>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="card-header px-0">
        <div className="card-title">
          <h4 className="fw-bolder">{lang.labels.statements}</h4>
        </div>
        {!readOnly &&
          <div className="card-toolbar">
            <button className="btn btn-sm btn-light-primary" type="button" onClick={addStatement}>{lang.labels.addStatement}</button>
          </div>
        }
      </div>
      <div className="pt-3">
        {statements.map((stmt, index) =>
          readOnly ? renderReadOnly(stmt, index) : renderEditable(stmt, index)
        )}
      </div>
    </div>
  )
}

export default StatementBuilder;
